"use client"

import { ArrowRight } from "lucide-react"
import { useTranslations } from "next-intl"
import { useState } from "react"

import { createStripeAccountLinkAction } from "@/app/[locale]/merchant/actions"
import { Button } from "@/components/ui/button"
import { CONST } from "@/constants"
import { useToast } from "@/hooks/use-toast"
import { Link } from "@/i18n/routing"
import type { MerchantProfile } from "@/lib/zenstack/models"
import { useServerAction } from "@/hooks/useServerAction"

export default function StripeConnection({ merchantProfile }: { merchantProfile: MerchantProfile }) {
  const t = useTranslations("merchant.stripe.settings")
  const [isLoading, setIsLoading] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const isConnected = merchantProfile.stripeAccountId !== null

  const { serverActionSync: createAccountLink } = useServerAction(createStripeAccountLinkAction)

  const { toast } = useToast()

  const handleCreateAccountLink = () => {
    setIsLoading(true)
    createAccountLink(undefined, (res) => {
      if (res) {
        if (res.success && res.data) {
          window.location.href = res.data.url
        } else {
          toast({ variant: "destructive", title: "Error", description: res.errors.join(", ") })
          setIsLoading(false)
        }
      }
    })
  }

  const renderConnectionStatus = () => {
    if (!merchantProfile.activated) {
      return <p className="text-sm font-medium text-muted-foreground">{t("verification")}</p>
    }

    return (
      <Button onClick={handleCreateAccountLink} className="w-full" disabled={isLoading}>
        {isLoading ? t("buttons.connecting") : t("buttons.connect")}
        <ArrowRight className="ml-2 size-4" />
      </Button>
    )
  }

  const renderAccountManagementLink = () => {
    return (
      <div className="flex flex-col gap-2">
        <Link href={CONST.STRIPE_DASHBOARD_URL} target="_blank" rel="noopener noreferrer" className="w-full">
          <Button className="w-full">
            {t("buttons.manage")}
            <ArrowRight className="ml-2 size-4" />
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-md p-6">
      <div className="mb-6">
        <p className="text-sm text-muted-foreground">{t("description")}</p>
      </div>

      <div className="mb-6">
        <p className="mb-4 text-sm text-muted-foreground">{t("info")}</p>
        <ul className="mb-4 list-inside list-disc space-y-1 text-sm text-muted-foreground">
          <li>{t("benefits.payments")}</li>
          <li>{t("benefits.reports")}</li>
          <li>{t("benefits.security")}</li>
        </ul>
        <p className="text-sm text-muted-foreground">{t("connection_required")}</p>
      </div>

      <div>{isConnected ? renderAccountManagementLink() : renderConnectionStatus()}</div>
    </div>
  )
}
